import { create } from "zustand";
import type { User } from "firebase/auth";
import { t } from "i18next";
import {
  signInUserWithEmail,
  signOutUser,
  sendPasswordResetWithEmail,
  getUser,
  addUser as addUserService,
  addUserOAuth,
  parseOAuthDisplayName,
  signInUserWithGoogle,
  linkGoogleAccountToCurrentUser,
  userHasGoogleProvider,
  userHasPasswordProvider,
  type UserData,
  type AddUserParameters,
  checkEmailVerified,
  getCurrentUser,
} from "@/services/auth";
import { FirebaseError } from "firebase/app";
import * as sentry from "@/services/sentry";
import { AUTH_ERRORS } from "@/common/errors";
import { AuthStatus } from "@/common/enums/enums";
import type { AxiosError } from "axios";
import type { ApiErrorResponse } from "@/lib/axios/axiosClient";

let oauthSignInInProgress = false;
let manualSignInInProgress = false;

export const isOAuthSignInInProgress = (): boolean => oauthSignInInProgress;

export interface AuthState {
  authStatus: AuthStatus;
  user: User | null;
  userData: null | UserData;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (options?: { occupationId?: number }) => Promise<void>;
  linkGoogleAccount: () => Promise<void>;
  reSignIn: (user: User | null) => Promise<void>;
  signInWithCurrentUser: () => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
  forgotPassword: (email: string) => Promise<void>;
  getUser: (email: string) => Promise<void>;
  addUser: ({
    email,
    firstname,
    lastname,
    password,
    occupationId,
  }: Omit<AddUserParameters, "fUid"> & { password: string }) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  (set, get): AuthState => ({
    authStatus: AuthStatus.LOADING,
    user: null,
    userData: null,
    loading: false,
    error: null,
    signIn: async (email: string, password: string): Promise<void> => {
      set({ loading: true, error: null });
      manualSignInInProgress = true;
      try {
        const userCredential = await signInUserWithEmail({ email, password });
        const { user } = await getUser({ email });
        const isEmailVerified = await checkEmailVerified();
        if (!user) {
          throw new Error(AUTH_ERRORS.USER_NOT_FOUND);
        }
        if (!isEmailVerified) {
          throw new Error(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
        }
        sentry.signIn({
          id: user?.id,
          fuid: user?.f_uid,
          email: user?.email,
          fullName: `${user?.firstname} ${user?.lastname}`,
        });
        set({
          user: userCredential.user,
          loading: false,
          userData: user,
          authStatus: AuthStatus.AUTHENTICATED,
        });
      } catch (error: unknown) {
        if (error instanceof FirebaseError) {
          set({
            user: null,
            error: error.code,
            loading: false,
            userData: null,
          });
        } else if (error instanceof Error) {
          set({
            user: null,
            error: error.message,
            loading: false,
            userData: null,
          });
        } else {
          set({
            user: null,
            userData: null,
            error: t("error.unknown"),
            loading: false,
          });
        }
        set({ authStatus: AuthStatus.UNAUTHENTICATED });
      } finally {
        manualSignInInProgress = false;
      }
    },
    signInWithGoogle: async (options?: {
      occupationId?: number;
    }): Promise<void> => {
      set({ loading: true, error: null });
      oauthSignInInProgress = true;
      try {
        const userCredential = await signInUserWithGoogle();
        const firebaseUser = userCredential.user;
        const email = firebaseUser.email;
        if (!email) {
          throw new Error(AUTH_ERRORS.INVALID_CREDENTIALS);
        }

        const parsedName = parseOAuthDisplayName(firebaseUser.displayName);
        await addUserOAuth({
          firstname: parsedName.firstname || undefined,
          lastname: parsedName.lastname || undefined,
          occupationId: options?.occupationId,
        });

        const { user } = await getUser({ email });
        if (!user) {
          throw new Error(AUTH_ERRORS.USER_NOT_FOUND);
        }

        if (user.f_uid && firebaseUser.uid !== user.f_uid) {
          throw new Error(AUTH_ERRORS.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL);
        }

        sentry.signIn({
          id: user.id,
          fuid: user.f_uid,
          email: user.email,
          fullName: `${user.firstname} ${user.lastname}`,
        });
        set({
          user: firebaseUser,
          loading: false,
          userData: user,
          authStatus: AuthStatus.AUTHENTICATED,
        });
      } catch (error: unknown) {
        await signOutUser().catch(() => undefined);

        if (error instanceof FirebaseError) {
          const code =
            error.code === "auth/popup-closed-by-user"
              ? AUTH_ERRORS.POPUP_CLOSED
              : error.code;
          set({
            user: null,
            error: code,
            loading: false,
            userData: null,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
          return;
        }

        const axiosError = error as AxiosError<ApiErrorResponse>;
        const apiCode = axiosError.response?.data?.error?.code;
        if (apiCode) {
          set({
            user: null,
            error: apiCode,
            loading: false,
            userData: null,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
          return;
        }

        if (error instanceof Error) {
          set({
            user: null,
            error: error.message,
            loading: false,
            userData: null,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
        } else {
          set({
            user: null,
            userData: null,
            error: t("error.unknown"),
            loading: false,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
        }
      } finally {
        oauthSignInInProgress = false;
      }
    },
    linkGoogleAccount: async (): Promise<void> => {
      set({ loading: true, error: null });
      oauthSignInInProgress = true;
      try {
        const linkedUser = await linkGoogleAccountToCurrentUser();
        const email = linkedUser.email;
        if (!email) {
          throw new Error(AUTH_ERRORS.INVALID_CREDENTIALS);
        }

        await addUserOAuth({});

        const { user: refreshedUserData } = await getUser({ email });
        if (refreshedUserData) {
          set({
            user: linkedUser,
            userData: refreshedUserData,
            loading: false,
            error: null,
          });
        } else {
          set({
            user: linkedUser,
            loading: false,
            error: null,
          });
        }
      } catch (error: unknown) {
        if (error instanceof FirebaseError) {
          const code =
            error.code === "auth/popup-closed-by-user"
              ? AUTH_ERRORS.POPUP_CLOSED
              : error.code;
          set({ loading: false, error: code });
          throw error;
        }
        if (error instanceof Error) {
          set({ loading: false, error: error.message });
          throw error;
        }
        set({ loading: false, error: t("error.unknown") });
        throw error;
      } finally {
        oauthSignInInProgress = false;
      }
    },
    signInWithCurrentUser: async (): Promise<void> => {
      set({ loading: true, error: null });
      try {
        const currentUser = await getCurrentUser();
        const isEmailVerified = await checkEmailVerified();
        if (!isEmailVerified) {
          throw new Error(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
        }
        const { user } = await getUser({ email: currentUser!.email! });
        sentry.signIn({
          id: user?.id,
          fuid: user?.f_uid,
          email: user?.email,
          fullName: `${user?.firstname} ${user?.lastname}`,
        });
        set({ user: currentUser, userData: user, loading: false });
      } catch (error: unknown) {
        let message: string;
        if (error instanceof FirebaseError) {
          message = error.code;
        } else if (error instanceof Error) {
          message = error.message;
        } else {
          message = t("error.unknown");
        }
        set({
          user: null,
          userData: null,
          error: message,
          loading: false,
        });
      }
    },
    signOut: async (): Promise<void> => {
      set({ authStatus: AuthStatus.LOADING });
      set({ loading: true, error: null });
      try {
        await signOutUser();
        sentry.signOut();
        set({ user: null, loading: false, userData: null });
      } catch (error: unknown) {
        if (error instanceof Error) {
          set({ user: null, error: error.message, loading: false });
        } else {
          set({
            user: null,
            error: t("error.unknown"),
            loading: false,
          });
        }
      } finally {
        set({ authStatus: AuthStatus.UNAUTHENTICATED });
      }
    },

    forgotPassword: async (email: string): Promise<void> => {
      set({ loading: true, error: null });
      try {
        await sendPasswordResetWithEmail({ email });
        set({ user: null, loading: false });
      } catch (error: unknown) {
        if (error instanceof Error) {
          set({ user: null, error: error.message, loading: false });
        } else {
          set({
            user: null,
            error: t("error.unknown"),
            loading: false,
          });
        }
      }
    },
    setUser: (user: User | null): void => {
      set({ user });
    },
    getUser: async (email: string): Promise<void> => {
      set({ loading: true, error: null });
      try {
        const { user } = await getUser({ email });
        sentry.signIn({
          id: user?.id,
          fuid: user?.f_uid,
          email: user?.email,
          fullName: `${user?.firstname} ${user?.lastname}`,
        });
        set({ userData: user, loading: false });
      } catch (error: unknown) {
        if (error instanceof Error) {
          set({ userData: null, error: error.message, loading: false });
        } else {
          set({
            user: null,
            error: t("error.unknown"),
            loading: false,
          });
        }
      }
    },
    addUser: async ({
      email,
      firstname,
      lastname,
      password,
      occupationId,
    }: Omit<AddUserParameters, "fUid"> & {
      password: string;
    }): Promise<void> => {
      set({ loading: true, error: null });
      try {
        await addUserService({
          email,
          firstname,
          lastname,
          password,
          occupationId,
        });
        set({ user: null, error: null, loading: false });
      } catch (error: unknown) {
        if (error instanceof Error) {
          set({ loading: false, error: "gibigi4144@ekuali.com", user: null });
        } else {
          set({ loading: false, error: "gibigi4144@ekuali.com", user: null });
        }
      }
    },
    reSignIn: async (userAuth: User | null): Promise<void> => {
      if (oauthSignInInProgress || manualSignInInProgress) {
        return;
      }

      const { authStatus, user, userData } = get();
      const isInitialBootstrap = authStatus === AuthStatus.LOADING;

      if (!userAuth) {
        set({
          authStatus: AuthStatus.UNAUTHENTICATED,
          user: null,
          userData: null,
        });
        return;
      }

      if (
        authStatus === AuthStatus.AUTHENTICATED &&
        user?.uid === userAuth.uid &&
        userData?.email === userAuth.email
      ) {
        return;
      }

      try {
        if (!userAuth.emailVerified || !userAuth.email) {
          throw new Error(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
        }

        const { user: dbUser } = await getUser({ email: userAuth.email });
        if (!dbUser) {
          throw new Error(AUTH_ERRORS.USER_NOT_FOUND);
        }

        if (dbUser.f_uid && userAuth.uid !== dbUser.f_uid) {
          const isGoogleOnlySession =
            userHasGoogleProvider(userAuth) &&
            !userHasPasswordProvider(userAuth);
          if (isGoogleOnlySession) {
            await signOutUser().catch(() => undefined);
            throw new Error(
              AUTH_ERRORS.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL
            );
          }
        }

        sentry.signIn({
          id: dbUser.id,
          fuid: dbUser.f_uid,
          email: dbUser.email,
          fullName: `${dbUser.firstname} ${dbUser.lastname}`,
        });
        set({
          user: userAuth,
          userData: dbUser,
          authStatus: AuthStatus.AUTHENTICATED,
        });
      } catch {
        if (isInitialBootstrap) {
          set({
            authStatus: AuthStatus.UNAUTHENTICATED,
            user: null,
            userData: null,
          });
        }
      }
    },
  })
);
